import React, { useState, useEffect } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Chip,
  Fade,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Analytics as AnalysisIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, selectActiveAnalysis, selectAnalysisProgress, setProgressModalOpen } from '../../store';

interface AnalysisStatusBarProps {
  onClose: () => void;
}

const AnalysisStatusBar: React.FC<AnalysisStatusBarProps> = ({ onClose }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const progress = useAppSelector(selectAnalysisProgress);
  
  const [currentToolIndex, setCurrentToolIndex] = useState(0);

  // Calculate overall progress
  const overallProgress = progress['overall']?.progress || 0;
  const overallStatus = progress['overall']?.status || 'pending';
  
  // Get current working tool
  const getCurrentTool = () => {
    if (!activeAnalysis?.selectedTools) return null;
    
    for (let i = 0; i < activeAnalysis.selectedTools.length; i++) {
      const tool = activeAnalysis.selectedTools[i];
      const toolProgress = progress[tool];
      if (toolProgress?.status === 'in_progress') {
        return tool;
      }
    }
    return null;
  };

  const currentTool = getCurrentTool();
  const completedTools = activeAnalysis?.selectedTools.filter(tool => 
    progress[tool]?.status === 'completed'
  ).length || 0;

  // Format tool name for display
  const formatToolName = (tool: string) => {
    const toolNames: Record<string, string> = {
      propertyParser: 'Property Parser',
      floorPlan: 'Floor Plan Analysis',
      cma: 'Market Analysis',
      neighborhood: 'Neighborhood Intelligence',
      pricing: 'Pricing Strategy',
      negotiation: 'Negotiation Support',
      listingDescription: 'Listing Description',
      clientConsultation: 'Client Consultation',
      leadQualification: 'Lead Qualification',
      transactionManagement: 'Transaction Management',
      preTourPrep: 'Pre-Tour Prep',
      clientMatch: 'Client Match',
      buyerSentiment: 'Buyer Sentiment',
      agentOrchestrator: 'Agent Orchestrator',
    };
    return toolNames[tool] || tool;
  };

  // Animate the current tool text
  useEffect(() => {
    if (currentTool) {
      const interval = setInterval(() => {
        setCurrentToolIndex(prev => (prev + 1) % 3);
      }, 800);
      return () => clearInterval(interval);
    }
  }, [currentTool]);

  const getAnimatedText = () => {
    if (!currentTool) return 'Initializing analysis...';
    const dots = '.'.repeat((currentToolIndex % 3) + 1);
    return `Analyzing with ${formatToolName(currentTool)}${dots}`;
  };

  // Handle click to open full modal
  const handleStatusBarClick = () => {
    dispatch(setProgressModalOpen(true));
  };

  if (!activeAnalysis || overallStatus === 'completed') {
    return null;
  }

  return (
    <Fade in={true}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(25, 25, 25, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.palette.primary.main}`,
          cursor: 'pointer',
        }}
        onClick={handleStatusBarClick}
      >
        {/* Progress Bar */}
        <LinearProgress
          variant="determinate"
          value={overallProgress}
          sx={{
            height: 3,
            backgroundColor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              transition: 'transform 0.3s ease',
            },
          }}
        />
        
        {/* Content */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 48,
          }}
        >
          {/* Left side - Status info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalysisIcon 
                color="primary" 
                fontSize="small"
                sx={{
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Typography variant="body2" fontWeight="medium" color="primary">
                Property Analysis
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              color="text.primary"
              sx={{
                minWidth: 200,
                transition: 'opacity 0.3s ease',
              }}
            >
              {getAnimatedText()}
            </Typography>
            
            <Chip
              label={`${completedTools}/${activeAnalysis.selectedTools.length} completed`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.75rem' }}
            />
          </Box>

          {/* Right side - Progress percentage and controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {Math.round(overallProgress)}%
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Open detailed progress">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setProgressModalOpen(true));
                  }}
                >
                  <PlayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Hide status bar">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default AnalysisStatusBar;